/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { sha256 } from 'sha.js';
import { Constants } from '../common/Constants';
import { ApprovalsLocalization } from '../localization';
import { RequestPageVersion, TeamsEnvironment } from '../service/gen';
import { isMobileRequestPageV2 } from './ECSConfigUtils';
import { getAppHost } from './TeamsUtils';
import { useUserSettings } from './UserSettings/UserSettingsUtils';

/**
 * removes duplicates from original elements, and returns the result
 */

export function removeDuplicates<T>(
    originals: T[],
    duplicates: T[],
    identical: (a: T, b: T) => boolean = (a: T, b: T): boolean => a === b
): T[] {
    if (originals && duplicates) {
        return originals.filter(
            (original: T): boolean =>
                duplicates.filter((duplicate: T): boolean => identical(original, duplicate)).length === 0
        );
    } else {
        return originals;
    }
}

/**
 * filter elements by the text provided, and returns the result
 */
export function filterByText<T>(elements: T[], text: string, compare: (a: T, text: string) => boolean): T[] {
    return elements.filter((element: T): boolean => {
        return compare(element, text);
    });
}

// A utility function such that all the promises in the chain are flushed immediately.
export const flushAllPromises = (): Promise<void> =>
    new Promise((resolve: () => void): void => {
        setTimeout(resolve, 0);
    });

/**
 * String format method, format string can have {0} or {key}
 *
 * @param formatString
 * @param params
 * @returns string formatted string
 */
export function stringFormat(...params: string[]): string;
export function stringFormat(formatString: string, params: string[] | { [key: string]: any }): string;
export function stringFormat(formatString: string, params: any): string {
    if (typeof params === 'string') {
        /* eslint-disable prefer-rest-params */
        formatString = arguments[0];
        params = [].splice.call(arguments, 0);
        /* eslint-enable prefer-rest-params */
        params = params.splice(1);
    }

    if (!formatString) {
        return '';
    }

    return formatString.replace(/\{(\w+)\}/g, (m: string, n: string): string => {
        return typeof params[n] !== 'undefined' ? params[n] : m;
    });
}

/**
 * Build query string from map of query parameter
 *
 * @param queryParams
 */
export function toQueryString(queryParams: object): string {
    const encodedQueryParams = [];
    for (const key in queryParams) {
        if (queryParams.hasOwnProperty(key)) {
            encodedQueryParams.push(key + '=' + encodeURIComponent(queryParams[key]));
        }
    }
    return encodedQueryParams.join('&');
}

declare const ENVIRONMENT: string;

/**
 * get Approvals app name
 *
 * @param loc approvals localization object
 * @returns approvals app name
 */
export const getAppName = (loc: ApprovalsLocalization): string =>
    ENVIRONMENT === 'prod'
        ? loc.strings.app_name
        : ENVIRONMENT === 'ppe'
        ? `${loc.strings.app_name} - Dogfood`
        : ENVIRONMENT === 'int'
        ? `${loc.strings.app_name} - Int`
        : ENVIRONMENT === 'dev'
        ? `${loc.strings.app_name} - Dev`
        : ENVIRONMENT === 'gcch'
        ? `${loc.strings.app_name}`
        : ENVIRONMENT === 'dod'
        ? `${loc.strings.app_name}`
        : ENVIRONMENT === 'gallatin'
        ? `${loc.strings.app_name}`
        : `${loc.strings.app_name} - ${ENVIRONMENT}`;

export const isCurrentEnvIn = (envs: string): boolean => {
    try {
        return envs.split(',').some((value: string): boolean => {
            return value === ENVIRONMENT || value.toLocaleLowerCase() === ENVIRONMENT;
        });
    } catch {
        return false;
    }
};

export const isTemplateEnabled = (): boolean => getAppHost().getEcsConfig().MicrosoftTeamApprovals.TemplateEnabled;
export const isSelfOwnedFormEnabled = (): boolean => ENVIRONMENT === 'gallatin';

export const isCurrentHostIn = (hosts: string, currentHost: string): boolean => {
    if (!hosts || !currentHost) {
        return false;
    }
    try {
        const lowerCurrentHost = currentHost.toLowerCase();
        return hosts
            .toLowerCase()
            .split(',')
            .some((value: string): boolean => {
                return value === lowerCurrentHost;
            });
    } catch {
        return false;
    }
};

/**
 * determine whether given env is a default flow environment
 *
 * @param name environment name
 * @param envs available environment list
 * @returns true if given env name is a default one, false otherwise
 */
export const isDefaultEnvironment = (name: string, envs: TeamsEnvironment[]): boolean => {
    if (!name || !envs) {
        return true;
    }

    const env = envs.filter((env: TeamsEnvironment): boolean => env.Name === name);
    if (env.length === 0) {
        return true;
    }

    return env.shift().Properties.IsDefault;
};

/**
 * Find the environment by name, if name is null/undefined/empty, default env would be returned. Would return null if no env found.
 *
 * @param name environment name
 * @param envs available environments
 * @returns
 */
export const findEnvironmentByName = (name: string, envs: TeamsEnvironment[]): TeamsEnvironment => {
    if (!envs) {
        return null;
    }

    if (!name) {
        // try find the default env if name is null/undefined
        return findDefaultEnvironment(envs);
    }

    return (
        envs
            .filter((env: TeamsEnvironment): boolean => {
                return env.Name === name;
            })
            .shift() || null
    );
};

export const findDefaultEnvironment = (envs: TeamsEnvironment[]): TeamsEnvironment => {
    if (!envs) {
        return null;
    }

    return (
        envs
            .filter((env: TeamsEnvironment): boolean => {
                return env.Properties?.IsDefault === true;
            })
            .shift() || null
    );
};

/**
 * Try parse the AFD ref string header from the response
 *
 * @param response the http response
 * @returns the AFD ref string, empty string would be returned if not found.
 */
export const tryParseAfdRefHeader = (response: Response): string => {
    const MsEdgeRefHeaderName = 'x-msedge-ref';
    const MsRefHeaderName = 'x-ms-ref';

    if (!response?.headers) {
        return '';
    }

    // headers.get() would return null if the header name is not found.
    const refStr = response.headers.get(MsEdgeRefHeaderName) ?? response.headers.get(MsRefHeaderName);
    return refStr ?? '';
};

/**
 * function to determine whether a language is displayed RTL
 *
 * @param locale name
 * @returns dir property of HTML
 * TODO TASK 1144925: Better way to decide whether a language is displayed rtl ot ltr
 */
export const getHTMLDir = (localeName: string): string => {
    const lang = localeName.split('-')[0];
    switch (lang) {
        case 'ar':
        case 'he':
            return 'rtl';
        default:
            return 'ltr';
    }
};

/**
 * function to get the request page version
 *
 * @returns RequestPageVersion
 */
export function useRequestPageVersion(): RequestPageVersion {
    const isMobile = getAppHost().isMobile();
    const userSettings = useUserSettings();
    return isMobile
        ? isMobileRequestPageV2()
            ? RequestPageVersion.V2_0
            : RequestPageVersion.V1_0
        : userSettings?.RequestPageVersion;
}

export function delay(time: number): Promise<void> {
    return new Promise((resolve: () => void): void => {
        setTimeout((): void => resolve(), time);
    });
}

/**
 * Gets the forms service endpoint
 */
export function getFormsEndpoint(): string {
    if (ENVIRONMENT === 'gcch') {
        return Constants.FORMS_ENDPOINT_GCCH;
    }

    if (ENVIRONMENT === 'dod') {
        return Constants.FORMS_ENDPOINT_DOD;
    }

    return Constants.FORMS_ENDPOINT;
}

/**
 * Gets the graph service endpoint
 */
export function getGraphEndpoint(): string {
    if (ENVIRONMENT === 'gcch') {
        return Constants.GRAPH_ENDPOINT_GCCH;
    }

    if (ENVIRONMENT === 'dod') {
        return Constants.GRAPH_ENDPOINT_DOD;
    }

    if (ENVIRONMENT === 'gallatin') {
        return Constants.GRAPH_ENDPOINT_GALLATIN;
    }

    return Constants.GRAPH_ENDPOINT;
}

/**
 * Gets the teams endpoint
 */
export function getTeamsEndpoint(): string {
    if (ENVIRONMENT === 'gcch') {
        return Constants.TEAMS_ENDPOINT_GCCH;
    }

    if (ENVIRONMENT === 'dod') {
        return Constants.TEAMS_ENDPOINT_DOD;
    }

    if (ENVIRONMENT === 'gallatin') {
        return Constants.TEAMS_ENDPOINT_GALLATIN;
    }

    return Constants.TEAMS_ENDPOINT;
}

export const setLocalStorageUserVariable = (key: string, value: string): void => {
    localStorage.setItem(getKeyWithUserPrefix(key), value);
};

export const getLocalStorageUserVariable = (key: string): string => {
    return localStorage.getItem(getKeyWithUserPrefix(key));
};

export const removeLocalStorageUserVariable = (key: string): void => {
    localStorage.removeItem(getKeyWithUserPrefix(key));
};

const getKeyWithUserPrefix = (key: string): string => {
    if (!key || key.trim().length === 0) {
        return '';
    }

    const userId = getAppHost().getUserObjectId();
    const userIdHash = new sha256().update(userId).digest('hex');
    return `${userIdHash}:${key}`;
};
